import { Info, PrimaryButton, SecondaryButton, Tooltip, Typography } from '@weareopenr/component-library';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { ButtonContainer } from '../../ButtonContainer/ButtonContainer';
import { IGlobalItem, Steps } from '../types';
import { FieldNamesMarkedBoolean, useForm } from 'react-hook-form';
import GlobalItemDetailsFields from '../../GlobalItemDetailsFields/GlobalItemDetailsFields';
import { FormVariant } from 'src/types/menuItemForm.types';
import styles from './GlobalItemDetailsForm.module.scss';
import { isProductSyncActive } from '../../../helpers/isProductSyncActive/isProductSyncActive';
import { EntitySource, GLOBAL_ITEM_UPDATE_NOTE } from '../../../constants';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { TMenuItemType } from 'src/types/menu.types';
import WarningBanner from 'src/components/WarningBanner/WarningBanner';

interface IGlobalItemDetailsForm {
  variant: FormVariant;
  item: Partial<IGlobalItem>;
  isSubmitting?: boolean;
  onSaveItem?: (
    data: Partial<IGlobalItem>,
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<IGlobalItem>>>,
    isDirty: boolean,
  ) => void;
  onCloseOrBack: (nextStep?: Steps) => void;
  setGlobalItem?: Dispatch<SetStateAction<Partial<IGlobalItem>>>;
  showImageField?: boolean;
  canShowEnhancedBundleFlag?: boolean;
}

const getTitle = (type: TMenuItemType, variant: FormVariant) => {
  if (variant === FormVariant.UPDATE) return 'Edit Details';
  if (type === 'MODIFIER') return 'Create Modifier Details';
  if (type === 'BUNDLE') return 'Create Bundle Details';
  return 'Create Product Details';
};

const getSubtitle = (type: TMenuItemType, variant: FormVariant) => {
  if (variant === FormVariant.UPDATE) return 'Edit product information';
  if (type === 'MODIFIER') return 'Add modifier information';
  if (type === 'BUNDLE') return 'Add bundle information';
  return 'Add product information';
};

export const GlobalItemDetailsForm = ({
  variant,
  item,
  isSubmitting,
  onSaveItem,
  onCloseOrBack,
  setGlobalItem,
  showImageField = false,
  canShowEnhancedBundleFlag = false,
}: IGlobalItemDetailsForm) => {
  const { data: externalSystems } = useExternalSystems();
  const isSyncActive = useMemo(
    () => isProductSyncActive(externalSystems) && item.type !== 'BUNDLE' && item.type !== 'NON-FOOD',
    [externalSystems],
  );

  const formFields = useForm<IGlobalItem>({
    defaultValues: {
      name: item.name,
      consumer_name: item.consumer_name,
      recipe_id: item.recipe_id,
      description: item.description,
      plu: item.plu,
      image: item.image || null,
      source: EntitySource.LIBRARY,
      age_verification: item.age_verification,
      is_hfss: item.is_hfss,
      party_size: item.party_size,
      is_enhanced_bundle: item.is_enhanced_bundle,
      ...(variant === FormVariant.CREATE && { ranges: item.ranges || [] }),
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { dirtyFields, isDirty },
    watch,
    setError,
    clearErrors,
  } = formFields;

  const menuSource = watch('source');

  useEffect(() => {
    if (menuSource === 'library') {
      clearErrors('ranges');
    }
  }, [menuSource, clearErrors]);

  const onSubmit = (data: Partial<IGlobalItem>) => {
    if (menuSource === 'ranges' && (!data.ranges || data.ranges.length === 0)) {
      setError('ranges', { message: 'Must select one or more ranges before you can create your menu' });
      return;
    }
    if (onSaveItem) {
      onSaveItem({ ...data }, dirtyFields, isDirty);
    } else {
      setGlobalItem({ ...data });
      onCloseOrBack(Steps.tax);
    }
  };

  return (
    <div className={styles['main-container']}>
      <div className={styles['title-container']}>
        <div className={styles['header-container']}>
          <Typography weight="semi-bold" size="xxlarge">
            {getTitle(item.type, variant)}
          </Typography>
          {variant === FormVariant.UPDATE && isSyncActive && (
            <span className={styles['tooltip']}>
              <Tooltip
                position="bottom"
                text="Some of the fields below containing product information cannot be edited as they have been taken from Assure65"
              >
                <Info />
              </Tooltip>
            </span>
          )}
        </div>
        <Typography>{getSubtitle(item.type, variant)}</Typography>
        {variant === FormVariant.UPDATE && <WarningBanner text={GLOBAL_ITEM_UPDATE_NOTE} />}
      </div>
      <form onSubmit={handleSubmit(onSubmit)} aria-label="details-form">
        <GlobalItemDetailsFields
          formFields={formFields}
          itemType={item.type}
          isProductSynced={variant === FormVariant.UPDATE && isSyncActive}
          variant={variant}
          showImageField={showImageField}
          canShowEnhancedBundleFlag={canShowEnhancedBundleFlag}
        />
        <ButtonContainer
          left={
            <SecondaryButton
              size="full-width"
              buttonStyle="outline"
              type="button"
              onClick={() => (variant === FormVariant.CREATE ? onCloseOrBack(Steps.type) : onCloseOrBack())}
            >
              {variant === FormVariant.CREATE ? 'Back' : 'Cancel'}
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting}>
              {variant === FormVariant.CREATE ? 'Continue' : 'Save'}
            </PrimaryButton>
          }
        />
      </form>
    </div>
  );
};
